import { STRAPI_URL, flattenStrapiEntry, parseErrorMessage, strapiAuthHeaders } from './strapi';

function asRecord(v: unknown): Record<string, unknown> | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function parseListBody(res: Response, body: unknown): Record<string, unknown>[] {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  if (Array.isArray(body)) return body as Record<string, unknown>[];
  const b = asRecord(body);
  if (b && Array.isArray(b.data)) return b.data as Record<string, unknown>[];
  return [];
}

export type DynamicEntityRow = Record<string, unknown> & { documentId: string };

export function normalizeDynamicRow(raw: Record<string, unknown>): DynamicEntityRow {
  const f = flattenStrapiEntry(raw);
  const documentId = String(f.documentId ?? raw.documentId ?? '');
  return { ...f, documentId };
}

export type StrapiPagination = {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
};

export type DynamicEntityListResult = {
  rows: DynamicEntityRow[];
  pagination: StrapiPagination | null;
};

function parsePagination(body: unknown): StrapiPagination | null {
  const b = asRecord(body);
  const meta = b ? asRecord(b.meta) : null;
  const pag = meta ? asRecord(meta.pagination) : null;
  if (!pag) return null;

  const page = Number(pag.page);
  const pageSize = Number(pag.pageSize);
  const pageCount = Number(pag.pageCount);
  const total = Number(pag.total);

  if (![page, pageSize, pageCount, total].every((n) => Number.isFinite(n))) return null;
  return { page, pageSize, pageCount, total };
}

/** Append one master-grid contains filter (AND-combined with other entries). */
function appendDynamicEntityContainsFilter(params: URLSearchParams, field: string, raw: string): void {
  const v = String(raw ?? '').trim();
  if (!v) return;

  if ((field === 'status' || field === 'active_status') && /^-?\d+$/.test(v)) {
    params.append(`filters[${field}][$eq]`, v);
    return;
  }

  /** Parameter uji (metoda / baku mutu): nama atau simbol pada relasi. */
  if (field === 'parameter_uji') {
    params.append('filters[$or][0][parameter_uji][nama_parameter][$containsi]', v);
    params.append('filters[$or][1][parameter_uji][simbol][$containsi]', v);
    return;
  }
  /** Parameter uji (master baris sendiri): nama atau simbol. */
  if (field === 'parameter') {
    params.append('filters[$or][0][nama_parameter][$containsi]', v);
    params.append('filters[$or][1][simbol][$containsi]', v);
    return;
  }
  if (field === 'grouping') {
    const parentFields = ['kode', 'kode_kategori', 'deskripsi1'] as const;
    parentFields.forEach((pf, i) => {
      params.append(`filters[$or][${i}][grouping][${pf}][$containsi]`, v);
    });
    return;
  }

  const parts = field.split('.');
  if (parts.length > 1) {
    const leaf = parts[parts.length - 1]!;
    const path = parts.slice(0, -1).map((p) => `[${p}]`).join('');
    if (leaf === 'documentId') {
      const ids = v.split(',').map((id) => id.trim()).filter(Boolean);
      if (ids.length > 1) {
        ids.forEach((id, i) => {
          params.append(`filters${path}[${leaf}][$in][${i}]`, id);
        });
        return;
      }
      if (ids[0]) {
        params.append(`filters${path}[${leaf}][$eq]`, ids[0]);
        return;
      }
    }
    params.append(`filters${path}[${leaf}][$containsi]`, v);
    return;
  }

  params.append(`filters[${field}][$containsi]`, v);
}

export async function strapiListDynamicEntityPage(
  jwt: string,
  apiPlural: string,
  opts?: {
    populate?: string;
    page?: number;
    pageSize?: number;
    /** Strapi REST v4: `filters[field][$containsi]` per key (combined with AND). */
    containsFilters?: Partial<Record<string, string>>;
    /** Strapi REST v4: `filters[field][$eq]` per key (combined with AND). */
    exactFilters?: Partial<Record<string, string>>;
  },
): Promise<DynamicEntityListResult> {
  const populate = opts?.populate ?? '*';
  const page = opts?.page ?? 1;
  const pageSize = opts?.pageSize ?? 10;

  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': String(page),
    sort: 'updatedAt:desc',
    ...(populate !== '{}'
      ? { populate }
      : {}),
  });
  if (opts?.containsFilters) {
    for (const [field, raw] of Object.entries(opts.containsFilters)) {
      appendDynamicEntityContainsFilter(params, field, String(raw ?? ''));
    }
  }
  if (opts?.exactFilters) {
    for (const [field, raw] of Object.entries(opts.exactFilters)) {
      const v = String(raw ?? '').trim();
      if (!v) continue;
      params.append(`filters[${field}][$eq]`, v);
    }
  }
  const res = await fetch(`${STRAPI_URL}/api/${encodeURIComponent(apiPlural)}?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const rows = parseListBody(res, body).map((r) => normalizeDynamicRow(r));
  return { rows, pagination: parsePagination(body) };
}

export async function strapiListDynamicEntity(
  jwt: string,
  apiPlural: string,
  populate = '*',
  pageSize: number = 100,
  skipTheRest: boolean = false,
): Promise<DynamicEntityRow[]> {
  /** Must not exceed Strapi `rest.maxLimit` (apps/backend/config/api.ts), otherwise rows are silently capped. */
  const first = await strapiListDynamicEntityPage(jwt, apiPlural, { populate, page: 1, pageSize });
  const pageCount = first.pagination?.pageCount ?? 1;
  if (pageCount <= 1) return first.rows;
  if (skipTheRest) return first.rows;
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) =>
      strapiListDynamicEntityPage(jwt, apiPlural, { populate, page: i + 2, pageSize }),
    ),
  );
  return [first.rows, ...rest.map((r) => r.rows)].flat();
}

export type SamplingChecklistSearchSection =
  | { apiPlural: 'referensis'; kodeKategori: string; mode: 'exact' | 'prefix' }
  | { apiPlural: 'sni-methods' }
  | { apiPlural: 'parameter-uji-metodas' };

/** Server contains-search for one checklist section when the loaded rows have no local match. */
export async function strapiSearchSamplingChecklistSection(
  jwt: string,
  section: SamplingChecklistSearchSection,
  query: string,
): Promise<DynamicEntityRow[]> {
  const q = query.trim();
  if (!q) return [];

  const params = new URLSearchParams({
    'pagination[pageSize]': '100',
    'pagination[page]': '1',
    populate: '*',
  });

  if (section.apiPlural === 'referensis') {
    params.append(
      section.mode === 'exact' ? 'filters[kode_kategori][$eq]' : 'filters[kode_kategori][$containsi]',
      section.kodeKategori,
    );
    for (const [index, field] of ['kode_kategori', 'kode', 'deskripsi1'].entries()) {
      params.append(`filters[$or][${index}][${field}][$containsi]`, q);
    }
  } else if (section.apiPlural === 'sni-methods') {
    params.append('filters[$or][0][kode_sni][$containsi]', q);
    params.append('filters[$or][1][judul][$containsi]', q);
  } else {
    params.append('filters[$or][0][kode_sni][$containsi]', q);
    params.append('filters[$or][1][satuan][$containsi]', q);
    params.append('filters[$or][2][parameter_uji][nama_parameter][$containsi]', q);
    params.append('filters[$or][3][parameter_uji][simbol][$containsi]', q);
  }

  const res = await fetch(`${STRAPI_URL}/api/${section.apiPlural}?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseListBody(res, body).map((row) => normalizeDynamicRow(row));
}

export type RelationWrite =
  | { connect: string[] }
  | number
  | string
  | null
  | undefined;

export function customerRelationWrite(documentId: string | undefined): RelationWrite {
  const t = documentId?.trim();
  if (!t) return undefined;
  return { connect: [t] };
}

export function userRelationWrite(
  documentId: string | null | undefined,
  numericId: number | null | undefined,
): RelationWrite {
  if (documentId?.trim()) return { connect: [documentId.trim()] };
  if (numericId != null && Number.isFinite(numericId)) return numericId;
  return undefined;
}

export async function strapiCreateDynamicEntity(
  jwt: string,
  apiPlural: string,
  data: Record<string, unknown>,
  populate = '*',
): Promise<DynamicEntityRow> {
  const qs = populate ? `?populate=${encodeURIComponent(populate)}` : '';
  const res = await fetch(`${STRAPI_URL}/api/${encodeURIComponent(apiPlural)}${qs}`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const b = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!b || typeof b !== 'object') throw new Error('Invalid Strapi create response');
  return normalizeDynamicRow(b);
}

export async function strapiUpdateDynamicEntity(
  jwt: string,
  apiPlural: string,
  documentId: string,
  data: Record<string, unknown>,
  populate = '*',
): Promise<DynamicEntityRow> {
  const qs = populate ? `?populate=${encodeURIComponent(populate)}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/${encodeURIComponent(apiPlural)}/${encodeURIComponent(documentId)}${qs}`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const b = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!b || typeof b !== 'object') throw new Error('Invalid Strapi update response');
  return normalizeDynamicRow(b);
}

export async function strapiDeleteDynamicEntity(jwt: string, apiPlural: string, documentId: string): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/${encodeURIComponent(apiPlural)}/${encodeURIComponent(documentId)}`,
    {
      method: 'DELETE',
      headers: strapiAuthHeaders(jwt),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}
